/*
 * Purpose: Builder shell — category-grouped activity nav (not flat wide tabs)
 * Last modified: 2026-06-26 | Agent
 * Completeness: 90
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PRODUCT_NAME } from '@/lib/productTerms';
import { Button } from '@/components/ui/button';
import { Plus, Wand2, Home } from 'lucide-react';
import { SetupBanner } from '@/components/setup/SetupBanner';
import { BuilderWorkspace } from '@/components/BuilderWorkspace';
import { BuilderActivityNav } from '@/components/BuilderActivityNav';
import { SecretManager } from '@/components/SecretManager';
import { WorkflowCreator } from '@/components/WorkflowCreator';
import { WorkflowManager } from '@/components/WorkflowManager';
import { TemplateLibrary } from '@/components/TemplateLibrary';
import { LogsViewer } from '@/components/LogsViewer';
import { WorkflowStats } from '@/components/WorkflowStats';
import { CommandPalette } from '@/components/CommandPalette';
import { WorkflowVersioning } from '@/components/WorkflowVersioning';
import { WorkflowScheduler } from '@/components/WorkflowScheduler';
import { WorkflowIntegration } from '@/components/WorkflowIntegration';
import { WorkflowAnalytics } from '@/components/WorkflowAnalytics';
import { WorkflowCollaboration } from '@/components/WorkflowCollaboration';
import { ExecutionMonitor } from '@/components/ExecutionMonitor';
import { ErrorDebugger } from '@/components/ErrorDebugger';
import { ActivityLog } from '@/components/ActivityLog';
import { WorkflowSettings } from '@/components/WorkflowSettings';
import { ConnectorLibraryBrowser } from '@/components/ConnectorLibraryBrowser';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { DEFAULT_BUILDER_PANEL, type BuilderPanelId } from '@/lib/productActivities';
import { useWorkflowStore } from '@/stores/workflowStore';

function BuilderPanelContent({
  panel,
  mapShortcutsActive,
}: {
  panel: BuilderPanelId;
  mapShortcutsActive: boolean;
}) {
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);

  switch (panel) {
    case 'builder':
      return <BuilderWorkspace shortcutsActive={mapShortcutsActive} />;
    case 'templates':
      return (
        <div className="max-w-4xl">
          <TemplateLibrary />
        </div>
      );
    case 'secrets':
      return <SecretManager />;
    case 'flows':
      return (
        <div className="max-w-2xl">
          <WorkflowManager />
        </div>
      );
    case 'logs':
      return (
        <div className="max-w-2xl">
          <LogsViewer workflowId={currentWorkflow?.id} />
        </div>
      );
    case 'stats':
      return (
        <div className="max-w-4xl">
          <WorkflowStats />
        </div>
      );
    case 'connections':
      return <ConnectorLibraryBrowser />;
    case 'versions':
      return (
        <div className="max-w-2xl">
          <WorkflowVersioning />
        </div>
      );
    case 'scheduler':
      return (
        <div className="max-w-2xl">
          <WorkflowScheduler />
        </div>
      );
    case 'integrations':
      return (
        <div className="max-w-4xl">
          <WorkflowIntegration />
        </div>
      );
    case 'analytics':
      return (
        <div className="max-w-5xl">
          <WorkflowAnalytics />
        </div>
      );
    case 'monitor':
      return (
        <div className="max-w-4xl">
          <ExecutionMonitor />
        </div>
      );
    case 'debug':
      return (
        <div className="max-w-4xl">
          <ErrorDebugger />
        </div>
      );
    case 'collaborate':
      return (
        <div className="max-w-4xl">
          <WorkflowCollaboration />
        </div>
      );
    case 'activity':
      return (
        <div className="max-w-2xl">
          <ActivityLog />
        </div>
      );
    case 'settings':
      return (
        <div className="max-w-2xl">
          <WorkflowSettings />
        </div>
      );
    default:
      return null;
  }
}

export default function WorkflowBuilder() {
  const navigate = useNavigate();
  const [activePanel, setActivePanel] = useState<BuilderPanelId>(DEFAULT_BUILDER_PANEL);
  const [creatorOpen, setCreatorOpen] = useState(false);

  const isMapPanel = activePanel === 'builder';

  return (
    <div className="h-screen min-h-0 flex flex-col bg-background overflow-hidden">
      <div className="shrink-0 border-b border-border bg-card px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-foreground truncate ab-brand-wordmark">
            {PRODUCT_NAME}
          </h1>
          <span className="hidden sm:inline text-xs text-muted-foreground">· Builder</span>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <Home className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Home</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/setup')}>
            <Wand2 className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Onboarding</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/compose')}>
            <span className="hidden sm:inline">{PRODUCT_TERMS.autoBlockFlowComposer}</span>
            <span className="sm:hidden">Compose</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCreatorOpen(true)}>
            <Plus className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">New flow</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </div>

      <SetupBanner />

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
        <BuilderActivityNav
          activePanel={activePanel}
          onSelectPanel={setActivePanel}
          onOpenCompose={() => navigate('/compose')}
          className="shrink-0 lg:w-56 lg:border-r"
        />

        <main
          className={
            isMapPanel
              ? 'flex-1 min-h-0 overflow-hidden p-3 sm:p-4'
              : 'flex-1 min-h-0 overflow-auto p-3 sm:p-4'
          }
        >
          <BuilderPanelContent panel={activePanel} mapShortcutsActive={isMapPanel} />
        </main>
      </div>

      <WorkflowCreator open={creatorOpen} onOpenChange={setCreatorOpen} />
      <CommandPalette />
    </div>
  );
}
